import React, { Fragment, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Heading, Typography, cn } from '@kiiwi/ui';
import dynamic from 'next/dynamic';
import AppointmentInfoBar from '@/components/organisms/AppointmentInfoBar/AppointmentInfoBar';
import { Trans, useTranslation } from '@/src/i18n';
import {
  SUPPORT_PHONE_NUMBER,
  SUPPORT_PHONE_NUMBER_HREF,
} from '@/src/constants';
import HeadingSection from './HeadingSection';
import { isValidTemporaryAppointment } from '@/components/utils/appointment';
import Link from '@/components/ui/not-existing-kiiwi-components/Link';

const RegisterForm = dynamic(() => import('./RegisterForm'));

type Props = {
  patientPhoneNumber?: string;
};

const Register = ({ patientPhoneNumber }: Props) => {
  const { t } = useTranslation();
  const temporaryAppointment = useSelector(
    (state: any) => state?.temporaryAppointments?.item,
  );
  const withTempRdv = useMemo(
    () => isValidTemporaryAppointment(temporaryAppointment),
    [temporaryAppointment],
  );

  return (
    <div
      className={cn(
        'mt-[--header-bar] min-h-[--content-height] text-neutral-primary',
        withTempRdv
          ? 'relative mx-auto flex flex-col items-center justify-start bg-white pt-6 md:pt-8'
          : 'grid w-full bg-surface-lighter md:grid-cols-[447px_1fr]',
      )}
    >
      {withTempRdv && <AppointmentInfoBar appointment={temporaryAppointment} />}
      <div
        className={cn(
          'flex flex-col border-r border-neutral bg-white p-6 max-md:px-4 [&_.appointment-info-bar]:!bg-transparent',
          withTempRdv
            ? 'mb-8 max-w-[653px] rounded-lg border max-md:mx-4'
            : 'border-r md:w-[447px]',
        )}
      >
        <HeadingSection />

        <div className="mb-6 grow">
          <Heading as="h1" variant="title-2" className="mb-1">
            <Trans
              i18nKey="new-to-maiia"
              components={{ highlight: <Fragment /> }}
            />
          </Heading>
          <Typography className="mb-8 text-body-2">
            {t('time-to-create-your-account')}
          </Typography>
          <RegisterForm phone={patientPhoneNumber} />
        </div>

        <div>
          <Typography variant="body-3" className="mt-3 text-neutral-secondary">
            {t('need_help_register_call_support')}
            <Link
              className="text-body-3"
              href={`tel:${SUPPORT_PHONE_NUMBER_HREF}`}
            >
              {SUPPORT_PHONE_NUMBER}
            </Link>
          </Typography>
        </div>
      </div>
      {!withTempRdv && (
        <div
          className="flex h-full grow items-center justify-center bg-surface-light-brand bg-contain bg-center bg-no-repeat bg-origin-content p-[8vw] max-md:hidden max-md:w-0"
          style={{
            backgroundImage:
              'url("/static/images/pages_illu/login-register.svg")',
          }}
        />
      )}
    </div>
  );
};

export default Register;
